import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import Cookies from 'js-cookie';
import { Auth } from './modules/auth/auth';
// import DashboardVue from './modules/dashboard/dashboard.vue';

// import Auth from '../views/login/login';

// Ampliamos el tipo RouteMeta para incluir roles requeridos
declare module 'vue-router' {
    interface RouteMeta {
        is_public?: boolean;
        requires_auth?: boolean;
        allowed_roles?: string[];
        /** M37.5: ruta disponible SIEMPRE dentro del dashboard, incluso sin
         *  membresías (el layout no se oculta y el splash no redirige). */
        always_available?: boolean;
    }
}


const routes: Array<RouteRecordRaw> = [

    {
        path: '/auth',
        name: 'Auth',
        redirect: '/auth/login',
        children: [
            {
                path: 'login',
                name: 'AuthLogin',
                component: () => import('./modules/auth/auth.vue'),
                meta: {
                    is_public: true,
                    requires_auth: false,
                    allowed_roles: []
                }
            }
        ]
        
    },

    // {
    //     path: '/',
    //     redirect: '/organizaciones',
    // },

    {
        path: '/',
        name: 'Dashboard',
        // M37.5/M37.6: home según perfiles. SYNC: la cookie sgi-user-profiles
        // ya está seteada porque el login espera Dashboard.splash.initialize()
        // antes de navegar acá.
        //   · sin perfiles (no memberships)          → sin-membresias
        //   · TODOS los perfiles = org:cos           → cos
        //   · sys:root / org:admin                   → membresias
        //   · sys:admin (ve Organizaciones)          → organizaciones
        //   · tiene perfiles pero sin sección visible → sin-secciones
        redirect: () => {

            const raw = Cookies.get('sgi-user-profiles');
            const profiles: string[] = raw ? JSON.parse(raw) : [];

            if (profiles.length === 0)
                return '/sin-membresias';

            if (profiles.every(p => p === 'org:cos'))
                return '/cos';

            if (profiles.some(p => p === 'sys:root' || p === 'org:admin'))
                return '/membresias';

            if (profiles.includes('sys:admin'))
                return '/organizaciones';

            // M40.3: space:admin aterriza en su bandeja de solicitudes
            // (con space seleccionado es su única sección propia)
            if (profiles.includes('space:admin'))
                return '/solicitudes';

            // M37.6c: org:cos mezclado con otros perfiles (ej. org:member)
            // también aterriza en COS — es su única sección usable
            if (profiles.includes('org:cos'))
                return '/cos';

            return '/sin-secciones';

        },
        component: () => import('./modules/dashboard/dashboard.vue'),
        children: [

            // GENERAL
            {
                path: 'organizaciones',
                name: 'OrganizationList',
                component: () => import('./modules/organizations/main/organization.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root', 'sys:admin']
                }
            },

            {
                path: 'organizaciones/:id',
                name: 'OrganizationDetail',
                component: () => import('./modules/organizations/details/organization.detail.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root', 'sys:admin']
                }
            },

            {
                path: 'espacios',
                name: 'SpaceList',
                component: () => import('./modules/spaces/space_admin.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    // M40.6: space:admin ve el árbol de su subárbol (SpaceExplorer)
                    allowed_roles: ['org:admin', 'space:admin']
                }
            },

            // MONITOREO
            {
                path: 'cos',
                name: 'Cos',
                component: () => import('./modules/cos/cos.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['org:cos', 'org:admin']
                }
            },

            // USUARIOS
            {
                path: 'membresias',
                name: 'UserMembershipList',
                component: () => import('./modules/user_memberships/user_membership.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    // M40.4: space:admin (acotado a su subárbol por el backend)
                    allowed_roles: ['sys:root', 'org:admin', 'space:admin']
                }
            },

            {
                path: 'solicitudes',
                name: 'RequestList',
                component: () => import('./modules/requests/request.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root', 'org:admin', 'space:admin']
                }
            },

            {
                path: 'invitaciones',
                name: 'InvitationList',
                component: () => import('./modules/invitations/invitation.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    // M40.4: space:admin (acotado a su subárbol por el backend)
                    allowed_roles: ['sys:root', 'org:admin', 'space:admin']
                }
            },

            {
                path: 'tipos-identificacion',
                name: 'IdentificationTypeList',
                component: () => import('./modules/identification_types/identification_type.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'perfiles',
                name: 'UserProfileList',
                component: () => import('./modules/user_profiles/user_profile.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'permisos',
                name: 'PermissionList',
                component: () => import('./modules/permissions/permission.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },


            // TIPOS
            {
                path: 'tipos-espacio',
                name: 'SpaceTypeList',
                component: () => import('./modules/space_types/space_type.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'paises',
                name: 'CountryList',
                component: () => import('./modules/countries/country.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            // VEHICLES
            {
                path: 'vehiculos-membresias',
                name: 'VehicleMembershipList',
                component: () => import('./modules/vehicle_memberships/vehicle_membership.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    // M40.4: space:admin (acotado a su subárbol por el backend)
                    allowed_roles: ['org:admin', 'space:admin']
                }
            },

            {
                path: 'vehiculos',
                name: 'VehicleList',
                component: () => import('./modules/vehicles/vehicle.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'vehiculos-tipos',
                name: 'VehicleTypeList',
                component: () => import('./modules/vehicle_types/vehicle_type.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'vehiculos-marcas',
                name: 'VehicleBrandList',
                component: () => import('./modules/vehicle_brands/vehicle_brand.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'vehiculos-modelos',
                name: 'VehicleModelList',
                component: () => import('./modules/vehicle_models/vehicle_model.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'vehiculos-perfiles',
                name: 'VehicleProfileList',
                component: () => import('./modules/vehicle_profiles/vehicle_profile.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },


            // DEVICES
            // {
            //     path: 'vehiculos-membresias',
            //     name: 'VehicleMembershipList',
            //     component: () => import('./modules/vehicle_memberships/vehicle_membership.vue'),
            //     meta: {
            //         is_public: false,
            //         requires_auth: true,
            //         allowed_roles: ['org:admin']
            //     }
            // },

            {
                path: 'activos-listado',
                name: 'AssetList',
                component: () => import('./modules/assets/asset.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'activos-emparejamiento',
                name: 'AssetPairingList',
                component: () => import('./modules/asset_pairings/asset_pairing.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'activos-categorias',
                name: 'AssetCategoryList',
                component: () => import('./modules/asset_categories/asset_category.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'activos-marcas',
                name: 'AssetBrandList',
                component: () => import('./modules/asset_brands/asset_brand.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            {
                path: 'activos-modelos',
                name: 'AssetModelList',
                component: () => import('./modules/asset_models/asset_model.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: ['sys:root']
                }
            },

            // {
            //     path: 'vehiculos-perfiles',
            //     name: 'VehicleProfileList',
            //     component: () => import('./modules/vehicle_profiles/vehicle_profile.vue'),
            //     meta: {
            //         is_public: false,
            //         requires_auth: true,
            //         allowed_roles: ['sys:root']
            //     }
            // },


            // SIN MEMBRESÍAS (M37.5: vista dedicada DENTRO del layout del
            // dashboard — reemplaza la pantalla nula full-screen de M37)
            {
                path: 'sin-membresias',
                name: 'NoMemberships',
                component: () => import('./modules/dashboard/no_memberships.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: [],
                    always_available: true
                }
            },

            // SIN SECCIONES (M37.6: tiene membresías en la org actual pero
            // ningún perfil le habilita secciones del aside — ej. org:member)
            {
                path: 'sin-secciones',
                name: 'NoSections',
                component: () => import('./modules/dashboard/no_sections.vue'),
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: [],
                    always_available: true
                }
            },

            // MI CUENTA (M37: self-service — cualquier usuario autenticado, con o sin membresías)
            // M37.5: layout con sidebar de secciones + sub-rutas (estilo GitHub settings)
            {
                path: 'configuracion',
                name: 'Settings',
                component: () => import('./modules/settings/settings.vue'),
                redirect: { name: 'SettingsProfile' },
                meta: {
                    is_public: false,
                    requires_auth: true,
                    allowed_roles: [],
                    always_available: true
                },
                children: [
                    {
                        path: 'perfil',
                        name: 'SettingsProfile',
                        component: () => import('./modules/settings/settings_profile.vue'),
                        meta: {
                            is_public: false,
                            requires_auth: true,
                            allowed_roles: [],
                            always_available: true
                        }
                    },
                    {
                        path: 'emails',
                        name: 'SettingsEmails',
                        component: () => import('./modules/settings/settings_emails.vue'),
                        meta: {
                            is_public: false,
                            requires_auth: true,
                            allowed_roles: [],
                            always_available: true
                        }
                    },
                    {
                        path: 'seguridad',
                        name: 'SettingsSecurity',
                        component: () => import('./modules/settings/settings_security.vue'),
                        meta: {
                            is_public: false,
                            requires_auth: true,
                            allowed_roles: [],
                            always_available: true
                        }
                    },
                ]
            },

            // OTHERS

            {
                path: 'acceso-denegado',
                name: 'AccessDenied',
                component: () => import('./modules/denied/denied.vue'),
                meta: {
                    is_public: true,
                    requires_auth: false,
                    allowed_roles: []
                }
            },

            {
                path: ':pathMatch(.*)*',
                name: 'NotFound',
                component: () => import('./modules/notfound/notfound.vue'),
            },

        ]
    },

    // {
    //     path: '/:pathMatch(.*)*',
    //     name: 'NotFound',
    //     component: () => import('./modules/notfound/notfound.vue'),
    // },

];

const router = createRouter({
    history: createWebHistory(),
    // base: process.env.BASE_URL,
    // base: 'http://localhost:5173',
    routes
});


router.beforeEach(async (to) => { // 1. Eliminamos 'next' de los argumentos

    const requires_auth = to.meta.requires_auth ?? false;
    const allowed_roles = (to.meta.allowed_roles as string[]) ?? [];
    
    const is_authenticated = await Auth.isAuthenticated();
    const is_authorized = await Auth.isAuthorized(allowed_roles);

    // console.log(to.name, requires_auth, allowed_roles, is_authenticated, is_authorized);

    if (requires_auth) {

        if (!is_authenticated) {
            return { name: 'AuthLogin' }; // 2. Retorno directo en lugar de next()
        }

        if (!is_authorized) {
            return { name: 'AccessDenied' }; // 3. Retorno directo
        }

    }

    // Evitar que un usuario ya logueado vuelva al Login
    if (to.name === "AuthLogin" && is_authenticated && is_authorized) {
        return { name: 'Dashboard' };
    }

    // 4. No es necesario retornar nada para permitir la navegación, 
    // pero retornar 'true' es explícito y correcto.
    return true; 
});

export default router;